import { useMemo, useRef, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Edit, Plus, Sprout, Trash2 } from 'lucide-react'

import {
    ALL_CATEGORIES,
    ALL_STATUSES,
    DEFAULT_STATUS,
    buildListParams,
    hasActiveFilters,
} from './-list-params'
import type {
    AgriCategory,
    AgriUnit,
    AgriculturalMaterial,
    ListAgriculturalMaterialsParams,
} from '@/lib/api/agricultural-materials-api'
import type { CategoryFilter, StatusFilter } from './-list-params'
import { PageBackButton } from '@/components/common/PageBackButton'
import { MaterialFormDialog } from '@/components/agricultural-materials/material-form-dialog'
import { Badge } from '@/components/ui/badge'
import { ClearFilterButton } from '@/components/common/clear-filter-button'
import { defineListFilters } from '@/components/common/filters/core'
import { selectFilter } from '@/components/common/filters/select-filter'
import { textFilter } from '@/components/common/filters/text-filter'
import { useListFilters } from '@/components/common/filters/use-list-filters'
import { ListFilterBar } from '@/components/common/ListFilterBar'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useAuth } from '@/hooks/use-auth'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'
import {
    useAgriculturalMaterials,
    useDeleteAgriculturalMaterial,
} from '@/hooks/use-agricultural-materials-query'
import { m } from '@/lib/paraglide/messages'

const PERMISSION = 'agricultural_materials.read'
const PER_PAGE = 50

/**
 * Bộ lọc khai một lần, suy ra: parser URL, mặc định, badge đếm, chip.
 *
 * `status` có mặc định KHÁC RỖNG ("đang sử dụng") nên giá trị đó không ghi vào
 * URL và cũng không tính là đang lọc — xoá vật tư chỉ là tắt, để lẫn vào danh
 * sách thì nhân viên xoá xong vẫn thấy nó nằm đó và tưởng xoá hỏng.
 */
const FILTERS = defineListFilters({
    q: textFilter({
        label: m.agri_material_search_label,
        placeholder: m.agri_material_search_placeholder,
    }),
    category: selectFilter<CategoryFilter>({
        label: m.agri_material_category,
        defaultValue: ALL_CATEGORIES,
        options: () => [
            { value: ALL_CATEGORIES, label: m.agri_category_all() },
            { value: 'fertilizer', label: m.agri_category_fertilizer() },
            { value: 'pesticide', label: m.agri_category_pesticide() },
        ],
    }),
    status: selectFilter<StatusFilter>({
        label: m.agri_material_status_filter_label,
        defaultValue: DEFAULT_STATUS,
        options: () => [
            { value: 'active', label: m.agri_material_is_active() },
            { value: 'inactive', label: m.agri_material_status_inactive() },
            { value: ALL_STATUSES, label: m.agri_material_status_all() },
        ],
    }),
})

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/agricultural-materials/',
)({
    component: AgriculturalMaterialsPage,
    validateSearch: FILTERS.validateSearch,
})

function categoryLabel(category: AgriCategory): string {
    return category === 'fertilizer'
        ? m.agri_category_fertilizer()
        : m.agri_category_pesticide()
}

function unitLabel(unit: AgriUnit): string {
    switch (unit) {
        case 'kg':
            return m.agri_unit_kg()
        case 'bag':
            return m.agri_unit_bag()
        case 'bottle':
            return m.agri_unit_bottle()
        case 'pack':
            return m.agri_unit_pack()
    }
}

function AgriculturalMaterialsPage() {
    const { hasPermission, arePermissionsLoaded } = useAuth()
    if (arePermissionsLoaded && !hasPermission(PERMISSION)) {
        return <ForbiddenState />
    }
    return <MaterialsContent />
}

function MaterialsContent() {
    const { hasPermission } = useAuth()
    const canWrite = hasPermission('agricultural_materials.write')
    const navigate = useNavigate({ from: '/agricultural-materials/' })
    const { values, barProps, page, setPage } = useListFilters({
        def: FILTERS,
        search: Route.useSearch(),
        onSearchChange: (next, opts) =>
            void navigate({
                search: () => next,
                replace: opts?.replace ?? false,
            }),
        columns: 3,
    })

    const [createOpen, setCreateOpen] = useState(false)
    const [editing, setEditing] = useState<AgriculturalMaterial | null>(null)
    const deleteMutation = useDeleteAgriculturalMaterial()
    const deletion = useWorkflowTracking('agricultural_material', {
        autoStart: false,
        operation: 'delete',
    })
    const deleteTarget = useRef('')

    // Map bộ lọc → tham số API. Để tường minh ở màn: mỗi màn map một kiểu.
    const params = useMemo<ListAgriculturalMaterialsParams>(
        () =>
            buildListParams({
                page,
                perPage: PER_PAGE,
                search: values.q,
                category: values.category,
                status: values.status,
            }),
        [page, values.q, values.category, values.status],
    )
    const { data, isLoading } = useAgriculturalMaterials(params)
    const items = data?.items ?? []
    // Rỗng vì bộ lọc ≠ rỗng vì chưa có dữ liệu. Trạng thái mặc định đã lọc sẵn
    // nên câu này xuất hiện ngay lần mở trang đầu — đúng ý đồ.
    const filtering = hasActiveFilters({
        search: values.q,
        category: values.category,
        status: values.status,
    })
    // Rỗng vì chưa có dữ liệu khác hẳn rỗng vì bộ lọc quá hẹp — trường hợp thứ
    // hai phải cho người dùng lối thoát ngay tại chỗ (R10). Trạng thái mặc định
    // đã là một bộ lọc nên câu này xuất hiện ngay lần mở đầu — cố ý.
    const emptyState = (
        <div className="flex flex-col items-center gap-3 py-8 text-center">
            <span className="text-sm text-muted-foreground">
                {filtering
                    ? m.agri_material_empty_filter()
                    : m.agri_material_empty()}
            </span>
            {barProps.activeCount > 0 || values.q ? (
                <ClearFilterButton active onClear={barProps.onClear} />
            ) : null}
        </div>
    )
    const total = data?.meta?.total ?? 0
    const perPage = data?.meta?.per_page ?? PER_PAGE
    const currentPage = data?.meta?.page ?? page

    const handleDelete = async (mat: AgriculturalMaterial) => {
        if (!window.confirm(m.agri_material_delete_confirm({ name: mat.name })))
            return
        if (deleteTarget.current !== mat.id) deletion.finish('closed')
        deleteTarget.current = mat.id
        try {
            await deletion.run(() => deleteMutation.mutateAsync(mat.id))
        } catch {
            /* Existing hook reports errors. */
        }
    }

    const colSpan = canWrite ? 7 : 6

    return (
        <div className="space-y-4 page-pad">
            <div className="flex items-center gap-2">
                <PageBackButton className="-my-2 md:-my-1" />
                <Sprout className="size-6 text-primary" />
                <div>
                    <h1 className="text-2xl font-semibold">
                        {m.agri_material_title()}
                    </h1>
                    <p className="text-sm text-muted-foreground">
                        {m.agri_material_subtitle()}
                    </p>
                </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                    <ListFilterBar {...barProps} />
                </div>
                {canWrite ? (
                    <Button onClick={() => setCreateOpen(true)}>
                        <Plus className="mr-1 size-4" />
                        {m.agri_material_add()}
                    </Button>
                ) : null}
            </div>

            {/* Desktop: bảng. Mobile: danh sách card (ẩn bảng dưới sm). */}
            <div className="hidden rounded-md border bg-card md:block">
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-16">
                                    {m.agri_material_col_image()}
                                </TableHead>
                                <TableHead>
                                    {m.agri_material_col_name()}
                                </TableHead>
                                <TableHead>
                                    {m.agri_material_col_category()}
                                </TableHead>
                                <TableHead>
                                    {m.agri_material_col_unit()}
                                </TableHead>
                                <TableHead>
                                    {m.agri_material_col_status()}
                                </TableHead>
                                <TableHead>
                                    {m.agri_material_col_external()}
                                </TableHead>
                                {canWrite ? (
                                    <TableHead className="text-right">
                                        {m.agri_material_col_actions()}
                                    </TableHead>
                                ) : null}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={colSpan}
                                        className="py-8 text-center text-muted-foreground"
                                    >
                                        {m.common_loading()}
                                    </TableCell>
                                </TableRow>
                            ) : items.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={colSpan}>
                                        {emptyState}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                items.map((mat) => (
                                    <TableRow key={mat.id}>
                                        <TableCell>
                                            <MaterialThumb material={mat} />
                                        </TableCell>
                                        <TableCell className="whitespace-normal font-medium">
                                            {mat.name}
                                            {mat.notes ? (
                                                <p className="text-xs font-normal text-muted-foreground">
                                                    {mat.notes}
                                                </p>
                                            ) : null}
                                        </TableCell>
                                        <TableCell>
                                            <CategoryBadge
                                                category={mat.category}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {unitLabel(mat.unit)}
                                        </TableCell>
                                        <TableCell>
                                            <StatusBadge
                                                active={mat.is_active}
                                            />
                                        </TableCell>
                                        <TableCell className="text-sm text-muted-foreground">
                                            {mat.is_external
                                                ? m.agri_material_external_yes()
                                                : m.agri_material_external_no()}
                                        </TableCell>
                                        {canWrite ? (
                                            <TableCell className="text-right">
                                                <div className="flex justify-end gap-1">
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        aria-label={m.agri_material_edit()}
                                                        onClick={() =>
                                                            setEditing(mat)
                                                        }
                                                    >
                                                        <Edit className="size-4" />
                                                    </Button>
                                                    {mat.is_active ? (
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            aria-label={m.agri_material_delete()}
                                                            onClick={() =>
                                                                handleDelete(
                                                                    mat,
                                                                )
                                                            }
                                                            disabled={
                                                                deleteMutation.isPending
                                                            }
                                                        >
                                                            <Trash2 className="size-4 text-red-500" />
                                                        </Button>
                                                    ) : null}
                                                </div>
                                            </TableCell>
                                        ) : null}
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            </div>

            {/* Mobile cards */}
            <div className="space-y-3 md:hidden">
                {isLoading ? (
                    <p className="py-8 text-center text-sm text-muted-foreground">
                        {m.common_loading()}
                    </p>
                ) : items.length === 0 ? (
                    emptyState
                ) : (
                    items.map((mat) => (
                        <Card key={mat.id}>
                            <CardContent className="flex gap-3 p-3">
                                <MaterialThumb material={mat} />
                                <div className="min-w-0 flex-1 space-y-1">
                                    <div className="flex items-start justify-between gap-2">
                                        <p className="font-medium">
                                            {mat.name}
                                        </p>
                                        <StatusBadge active={mat.is_active} />
                                    </div>
                                    <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                                        <CategoryBadge
                                            category={mat.category}
                                        />
                                        <span>{unitLabel(mat.unit)}</span>
                                    </div>
                                    {mat.notes ? (
                                        <p className="text-xs text-muted-foreground">
                                            {mat.notes}
                                        </p>
                                    ) : null}
                                    {canWrite ? (
                                        <div className="flex gap-1 pt-1">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => setEditing(mat)}
                                            >
                                                <Edit className="mr-1 size-4" />
                                                {m.agri_material_edit()}
                                            </Button>
                                            {mat.is_active ? (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() =>
                                                        handleDelete(mat)
                                                    }
                                                    disabled={
                                                        deleteMutation.isPending
                                                    }
                                                >
                                                    <Trash2 className="mr-1 size-4 text-red-500" />
                                                    {m.agri_material_delete()}
                                                </Button>
                                            ) : null}
                                        </div>
                                    ) : null}
                                </div>
                            </CardContent>
                        </Card>
                    ))
                )}
            </div>

            <div className="flex items-center justify-between">
                <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                >
                    {m.common_prev_page()}
                </Button>
                <div className="text-sm text-muted-foreground">
                    {m.common_page()} {currentPage}
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    disabled={!total || currentPage * perPage >= total}
                    onClick={() => setPage(page + 1)}
                >
                    {m.common_next_page()}
                </Button>
            </div>

            <MaterialFormDialog
                open={createOpen}
                onOpenChange={setCreateOpen}
                material={null}
            />
            <MaterialFormDialog
                open={Boolean(editing)}
                onOpenChange={(v) => !v && setEditing(null)}
                material={editing}
            />
        </div>
    )
}

function MaterialThumb({ material }: { material: AgriculturalMaterial }) {
    if (material.image_url) {
        return (
            <img
                src={material.image_url}
                alt={material.name}
                className="size-12 rounded-md border object-cover"
                loading="lazy"
            />
        )
    }
    return (
        <div className="flex size-12 items-center justify-center rounded-md border bg-muted/30 text-muted-foreground">
            <Sprout className="size-5 stroke-1" />
        </div>
    )
}

function CategoryBadge({ category }: { category: AgriCategory }) {
    return category === 'fertilizer' ? (
        <Badge
            variant="outline"
            className="border-green-300 bg-green-50 text-green-700"
        >
            {categoryLabel(category)}
        </Badge>
    ) : (
        <Badge
            variant="outline"
            className="border-amber-300 bg-amber-50 text-amber-700"
        >
            {categoryLabel(category)}
        </Badge>
    )
}

function StatusBadge({ active }: { active: boolean }) {
    return active ? (
        <Badge className="bg-green-100 text-green-700">
            {m.agri_material_is_active()}
        </Badge>
    ) : (
        <Badge variant="outline">{m.agri_material_status_inactive()}</Badge>
    )
}

function ForbiddenState() {
    return (
        <Card>
            <CardContent className="space-y-2 py-12 text-center">
                <h2 className="text-lg font-semibold">
                    {m.agri_material_forbidden_title()}
                </h2>
                <p className="text-sm text-muted-foreground">
                    {m.agri_material_forbidden_desc()}
                </p>
            </CardContent>
        </Card>
    )
}
